import { SelectableService } from '../services/selectable.service';
import { type SelectableListDirective } from '../directives/selectable-list.directive';

/**
 * Sibling lists that read as one: a list that continues another, the way the calendar's sticky events sit
 * above its date-range list. The members share one selection — the chain is the root of their family, a
 * `SelectableService` with no rows of its own — and the arrow keys walk from the edge of one member into
 * the next. Order is the members' order in the document, so a chain is never told which list comes first.
 *
 * The owner (the component that renders the members) creates the chain and passes it to each member, and
 * destroys it when it goes.
 */
export class SelectableListChain<DATA = unknown> {
  /** The family root; each member connects its own service to it, which is what makes the selection one. */
  public readonly service = new SelectableService<DATA>();

  private readonly members = new Set<SelectableListDirective<DATA>>();

  public join(list: SelectableListDirective<DATA>): void {
    this.members.add(list);
  }

  public leave(list: SelectableListDirective<DATA>): void {
    this.members.delete(list);
  }

  /** The nearest member above `list` that has rows, or `undefined` at the top of the chain. */
  public previous(list: SelectableListDirective<DATA>): SelectableListDirective<DATA> | undefined {
    return this.ordered()
      .filter(
        (member) => member.hostElement.compareDocumentPosition(list.hostElement) & Node.DOCUMENT_POSITION_FOLLOWING,
      )
      .at(-1);
  }

  /** The nearest member below `list` that has rows, or `undefined` at the bottom of the chain. */
  public next(list: SelectableListDirective<DATA>): SelectableListDirective<DATA> | undefined {
    return this.ordered().find(
      (member) => member.hostElement.compareDocumentPosition(list.hostElement) & Node.DOCUMENT_POSITION_PRECEDING,
    );
  }

  /** Whether `node` is inside any member. The keyboard stays with the chain wherever focus sits within it. */
  public contains(node: Node): boolean {
    return [...this.members].some((member) => member.hostElement.contains(node));
  }

  /** Whether `node` is in a focus area registered on any member (see `SelectableListDirective.addFocusArea`). */
  public inFocusArea(node: Node): boolean {
    return [...this.members].some((member) => member.ownsFocusArea(node));
  }

  public destroy(): void {
    this.members.clear();
    this.service.destroy();
  }

  /** The members that have rows, in document order. A member with nothing to select is stepped over. */
  private ordered(): SelectableListDirective<DATA>[] {
    return [...this.members]
      .filter((member) => member.selectableService.items.value.length > 0)
      .sort((a, b) =>
        a.hostElement.compareDocumentPosition(b.hostElement) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
      );
  }
}
